import { useState } from "react";
import {
    Users, Check, X, ShieldCheck, ShieldPlus,
    LayoutDashboard, Lightbulb, Cpu, ShoppingCart, FolderKanban, LifeBuoy,
    UserX,
} from "lucide-react";

/* ---------------------------------------------------------
   Config
--------------------------------------------------------- */
const MODULES = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "registration", label: "Registration", icon: Lightbulb },
    { key: "experts", label: "Experts", icon: Cpu },
    { key: "bookings", label: "Bookings", icon: ShoppingCart },
    { key: "payments", label: "Payments", icon: FolderKanban },
    { key: "support", label: "Support", icon: LifeBuoy },
    { key: "reports", label: "Reports", icon: FolderKanban },
];

const ROLES = [
    { key: "super_admin", label: "Admin", desc: "Full access to every module" },
    { key: "manager", label: "Manager", desc: "Manage operations, no billing access" },
    { key: "support", label: "Support Executive", desc: "Handle tickets and bookings only" },
    { key: "viewer", label: "Viewer", desc: "Read-only access" },
];

const AVATAR_COLORS = ["bg-indigo-500", "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-violet-500"];

// In a real app these would come from the Onboarding component / a shared
// data source (API, context, props). Seeded here so this component works standalone.
const SEED_EMPLOYEES = [
    { id: 1, name: "Rahul Varma", email: "rahul.varma@apic.in", status: "active", role: "manager", modules: ["dashboard", "bookings", "payments"] },
    { id: 2, name: "Sneha Kulkarni", email: "sneha.k@apic.in", status: "active", role: "support", modules: ["support", "bookings"] },
    { id: 3, name: "Arjun Menon", email: "arjun.menon@apic.in", status: "pending", role: "", modules: [] },
    { id: 4, name: "Divya Shah", email: "divya.shah@apic.in", status: "pending", role: "", modules: [] },
];

function initials(name) {
    return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

/* ---------------------------------------------------------
   Small building blocks
--------------------------------------------------------- */
function StatusPill({ status }) {
    const map = {
        active: "bg-emerald-50 text-emerald-700",
        pending: "bg-amber-50 text-amber-700",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${map[status]}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${status === "active" ? "bg-emerald-500" : "bg-amber-500"}`} />
            {status === "active" ? "Active" : "Not active"}
        </span>
    );
}

function StatCard({ label, value, icon: Icon, tint }) {
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between">
            <div>
                <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>
                <p className="text-2xl font-bold text-slate-900">{value}</p>
            </div>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tint}`}>
                <Icon size={18} />
            </div>
        </div>
    );
}

function Modal({ title, subtitle, onClose, children, width = "max-w-lg" }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
            <div className={`relative bg-white w-full ${width} rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto`}>
                <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100 sticky top-0 bg-white">
                    <div>
                        <h2 className="text-base font-bold text-slate-900">{title}</h2>
                        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition p-1">
                        <X size={18} />
                    </button>
                </div>
                <div className="px-6 py-5">{children}</div>
            </div>
        </div>
    );
}

/* ---------------------------------------------------------
   Add Role modal — pick an onboarded employee, assign role + modules
--------------------------------------------------------- */
function AddRoleModal({ employees, onClose, onSave }) {
    const [step, setStep] = useState(employees.length ? "pick" : "empty");
    const [selectedId, setSelectedId] = useState(null);
    const [role, setRole] = useState("");
    const [modules, setModules] = useState([]);

    const selected = employees.find((e) => e.id === selectedId);

    const pick = (emp) => {
        setSelectedId(emp.id);
        setRole(emp.role || "");
        setModules(emp.modules || []);
        setStep("assign");
    };

    const toggleModule = (key) =>
        setModules((m) => (m.includes(key) ? m.filter((k) => k !== key) : [...m, key]));

    const handleSave = () => {
        if (!selected || !role) return;
        onSave(selected.id, role, modules);
        onClose();
    };

    return (
        <Modal
            title={step === "assign" ? "Assign role & modules" : "Select an employee"}
            subtitle={
                step === "assign"
                    ? `Setting access for ${selected?.name}`
                    : "Choose who you're assigning a role to"
            }
            onClose={onClose}
            width="max-w-2xl"
        >
            {step === "empty" && (
                <div className="text-center py-8">
                    <p className="text-sm text-slate-500">No onboarded employees yet.</p>
                    <p className="text-xs text-slate-400 mt-1">Add someone in the Onboarding component first.</p>
                </div>
            )}

            {step === "pick" && (
                <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                    {employees.map((emp, i) => (
                        <button
                            key={emp.id}
                            onClick={() => pick(emp)}
                            className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition text-left"
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-9 h-9 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} text-white flex items-center justify-center text-xs font-bold shrink-0`}>
                                    {initials(emp.name)}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-slate-800 truncate">{emp.name}</div>
                                    <div className="text-xs text-slate-400 truncate">{emp.email}</div>
                                </div>
                            </div>
                            <StatusPill status={emp.status} />
                        </button>
                    ))}
                </div>
            )}

            {step === "assign" && selected && (
                <div>
                    <div className="flex items-center gap-3 mb-6 pb-5 border-b border-slate-100">
                        <div className={`w-11 h-11 rounded-full ${AVATAR_COLORS[employees.findIndex((e) => e.id === selected.id) % AVATAR_COLORS.length]} text-white flex items-center justify-center text-sm font-bold shrink-0`}>
                            {initials(selected.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-slate-900">{selected.name}</div>
                            <div className="text-xs text-slate-400">{selected.email}</div>
                        </div>
                        <button
                            onClick={() => setStep("pick")}
                            className="text-xs font-semibold text-indigo-600 hover:underline shrink-0"
                        >
                            Change
                        </button>
                    </div>

                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Role</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                        {ROLES.map((r) => (
                            <button
                                key={r.key}
                                onClick={() => setRole(r.key)}
                                className={`text-left px-4 py-3 rounded-xl border transition ${role === r.key ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:border-slate-300"
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-slate-800">{r.label}</span>
                                    {role === r.key && <Check size={15} className="text-indigo-600" />}
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5">{r.desc}</p>
                            </button>
                        ))}
                    </div>

                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Module access</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                        {MODULES.map((m) => {
                            const Icon = m.icon;
                            const active = modules.includes(m.key);
                            return (
                                <button
                                    key={m.key}
                                    onClick={() => toggleModule(m.key)}
                                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition ${active ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:border-slate-300"
                                        }`}
                                >
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                                        <Icon size={14} />
                                    </div>
                                    <span className={`text-sm font-medium ${active ? "text-indigo-700" : "text-slate-600"}`}>
                                        {m.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <span className="text-xs text-slate-400">
                            {modules.length} module{modules.length !== 1 ? "s" : ""} selected
                        </span>
                        <div className="flex items-center gap-3">
                            <button onClick={onClose} className="text-sm font-semibold text-slate-500 px-4 py-2.5 hover:text-slate-800 transition">
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={!role}
                                className={`flex items-center gap-2 text-sm font-semibold rounded-lg px-5 py-2.5 transition ${role ? "text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:opacity-90" : "text-slate-400 bg-slate-100 cursor-not-allowed"
                                    }`}
                            >
                                <ShieldCheck size={15} /> Save access
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
}

/* ---------------------------------------------------------
   Roles & Modules — standalone component
--------------------------------------------------------- */
export default function RolesAndModules() {
    const [employees, setEmployees] = useState(SEED_EMPLOYEES);
    const [showAddRole, setShowAddRole] = useState(false);

    const updateAssignment = (id, role, modules) =>
        setEmployees((list) =>
            list.map((e) => (e.id === id ? { ...e, role, modules, status: role ? "active" : "pending" } : e))
        );

    const assignedCount = employees.filter((e) => e.role).length;

    return (
        <div className="min-h-full bg-gray-50 p-6 lg:p-8">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-1">Team</p>
                    <h1 className="text-2xl font-bold text-slate-900">Roles & modules</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {employees.length} onboarded · {assignedCount} with a role assigned
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StatCard label="ONBOARDED EMPLOYEES" value={employees.length} icon={Users} tint="bg-indigo-50 text-indigo-600" />
                    <StatCard label="ROLES ASSIGNED" value={assignedCount} icon={ShieldCheck} tint="bg-emerald-50 text-emerald-600" />
                    <StatCard label="AWAITING ROLE" value={employees.length - assignedCount} icon={UserX} tint="bg-amber-50 text-amber-600" />
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between gap-3 p-5 border-b border-slate-200 flex-wrap">
                        <div className="flex items-center gap-2">
                            <ShieldCheck size={16} className="text-slate-400" />
                            <h2 className="text-sm font-bold text-slate-900">Roles &amp; module assignments</h2>
                        </div>
                        <button
                            onClick={() => setShowAddRole(true)}
                            className="flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg px-4 py-2 hover:opacity-90 transition"
                        >
                            <ShieldPlus size={15} /> Add role
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">
                                    <th className="px-5 py-3">Employee</th>
                                    <th className="px-5 py-3">Role</th>
                                    <th className="px-5 py-3">Modules</th>
                                    <th className="px-5 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employees.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="px-5 py-10 text-center text-sm text-slate-400">
                                            No employees onboarded yet.
                                        </td>
                                    </tr>
                                )}
                                {employees.map((emp, i) => (
                                    <tr key={emp.id} className="border-t border-slate-100">
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} text-white flex items-center justify-center text-[11px] font-bold shrink-0`}>
                                                    {initials(emp.name)}
                                                </div>
                                                <span className="font-medium text-slate-700">{emp.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3.5 text-slate-600">
                                            {emp.role ? ROLES.find((r) => r.key === emp.role)?.label : "—"}
                                        </td>
                                        <td className="px-5 py-3.5">
                                            {emp.modules.length === 0 ? (
                                                <span className="text-slate-400">—</span>
                                            ) : (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {emp.modules.map((mk) => (
                                                        <span key={mk} className="text-xs font-medium text-indigo-700 bg-indigo-50 rounded-md px-2 py-0.5">
                                                            {MODULES.find((m) => m.key === mk)?.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3.5"><StatusPill status={emp.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {showAddRole && (
                <AddRoleModal
                    employees={employees}
                    onClose={() => setShowAddRole(false)}
                    onSave={updateAssignment}
                />
            )}
        </div>
    );
}